import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useMutation } from 'react-query'
import { AxiosError } from 'axios'

import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import { Add as AddIcon } from '@material-ui/icons'

import { useQueryPaginator } from 'src/components/Table'
import { AuditDetailTable } from 'src/components/Table/detail/Audit'
import { AuditDialog } from 'src/modules/supply_chain/components/AuditDialog'
import { AuditResultPlaceAddDialog } from 'src/modules/supply_chain/components/AuditResultPlaceAddDialog'
import { Workflow } from 'src/modules/supply_chain/components/Workflow'
import { Notification } from 'src/components/Notification'

import { skuit } from 'src/services/skuit'
import { useQuery } from 'react-query'

import { AuditResultPlaceListTable } from 'src/components/Table/list/AuditResultPlace'
import { AuditResultUnitLocListTable } from 'src/components/Table/list/AuditResultUnitLoc'
import { AuditResultSkuListTable } from 'src/components/Table/list/AuditResultSku'
import { BatchListTable } from 'src/components/Table/list/Batch'
import { ContainerListTable } from 'src/components/Table/list/Container'
import { PlaceListTable } from 'src/components/Table/list/Place'

import { DetailPage } from 'src/components/Pages/DetailPage'
import { DetailTab } from 'src/components/Pages/Tab'
import { Audit } from 'src/api/models'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

interface PathProps {
  guid: string
}


interface DetailsTabProps {
  audit?: Audit
}

const DetailsTab = (props: DetailsTabProps) => {
  const { audit } = props
  const { guid } = useParams<PathProps>()
  const [open, setOpen] = useState(false)


  const placePaginator = useQueryPaginator('audit-places', skuit.sc.places.list, {
    params: { audits: guid, page_size: 20 },
  })

  const { mutate: removePlaces } = useMutation((places: string[]) => skuit.sc.audits.places.bulkRemove(audit?.guid as string, {places}), {
    onSuccess: () => {
      toast.success(() => <Notification message={`Successfully created Orders from csv`} />)
      placePaginator.refresh()
    },
    onError: (error: AxiosError) => {
      toast.error(() => <Notification message={`Failed to process CSV: ${error}`} />)
    },
  })

  return (
    <DetailTab>
      <AuditResultPlaceAddDialog
        audit={audit}
        open={open}
        handleClose={() => setOpen(false)}
      />
      <PlaceListTable
        title="Places in Audit"
        paginator={placePaginator}
        fields={['path']}
        extraActions={[{ title: 'Add Places', icon: <AddIcon />, onClick: () => setOpen(true) }]}
        extraBulkActions={[{ label: 'Remove From Audit', action: (selected) => {
          const places = selected.map((place) => place.guid)
          removePlaces(places)
        }}]}
      />
    </DetailTab>
  )
}

const ResultTab = () => {
  const { guid } = useParams<PathProps>()
  const skuPaginator = useQueryPaginator(
    'audit-result-skus',
    (config) => skuit.sc.audits.skus.list(guid, config),
    {
      params: {
        page_size: 10,
        ordering: 'sku',
      },
    }
  )
  const containerPaginator = useQueryPaginator(
    'audit-result-containers',
    skuit.sc.containers.list,
    {
      params: {
        resulting_audits: guid,
        page_size: 10,
        ordering: 'xid',
      },
    }
  )
  const unitPaginator = useQueryPaginator(
    'audit-result-inventory',
    (config) => skuit.sc.audits.units.list(guid, config),
    {
      params: {
        audits: guid,
        page_size: 10,
        ordering: 'sku_sku',
      },
    }
  )
  const batchPaginator = useQueryPaginator(
    'audit-result-batches',
    (config) => skuit.sc.batches.list(config),
    {
      params: {
        audits: guid,
        page_size: 10,
      },
    }
  )
  return (
    <DetailTab>
      <AuditResultSkuListTable
        paginator={skuPaginator}
        title="SKU Totals in Audit"
        fields={[
          'sku',
          'product',
          'numUnitsInAudit',
          'numAvailable',
          'numReserved',
          'numAwaitingShipment',
        ]}
      />
      <ContainerListTable
        paginator={containerPaginator}
        title="Containers Scanned in Audit"
        fields={['name', 'containerType', 'path']}
      />
      <AuditResultUnitLocListTable
        paginator={unitPaginator}
        title="Inventory Scanned in Audit"
        fields={['sku', 'product', 'batch', 'container', 'numUnits']}
      />
      <BatchListTable
        paginator={batchPaginator}
        title="Batches Scanned in Audit"
        fields={['batchType', 'clientId', 'client', 'expiresOn']}
      />
    </DetailTab>
  )
}

const DiffTab = () => {
  const { guid } = useParams<PathProps>()

  const diffPaginator = useQueryPaginator('audit-places', skuit.sc.audits.diff, {
    params: { audits: guid, page_size: 20 },
  })

  return (
    <DetailTab>
      <AuditResultPlaceListTable
        paginator={diffPaginator}
        title="Place Diff"
        fields={[
          'place',
          'path',
          'matched',
          'over',
          'under',
          'accuracyScore',
        ]}
      />
    </DetailTab>
  )
}

export const AuditPage = () => {
  const { guid } = useParams<PathProps>()
  const [createOpen, setCreateOpen] = useState(false)

  const audit = useQuery(['audit-details', guid], () => skuit.sc.audits.retrieve(guid))

  const refresh = () => {
    audit.refetch()
  }

  return (
    <>
      {createOpen && (
        <AuditDialog
          open={createOpen}
          handleClose={() => {
            setCreateOpen(false)
          }}
        />
      )}
      <DetailPage
        obj={audit}
        title="Audits"
        refresh={refresh}
        breadcrumbs={[
          { title: 'Dashboard', url: '/' },
          { title: 'Audits', url: '/inventory/audits' },
        ]}
        detailComponent={<AuditDetailTable obj={audit} refresh={refresh} />}
        secondaryComponent={
          <Workflow
            title="Audit Workflow"
            loading={audit.isFetching}
            workflow={audit.data?.workflow || []}
            status={audit.data?.workflowStatus || ''}
            actions={audit.data?.getWorkflowActions(() => audit.refetch()) || {}}
          />
        }
        menuItems={[
          {
            Icon: AddIcon,
            actions: [{ title: 'New Audit', onClick: () => setCreateOpen(true) }],
          },
        ]}
        tabs={['Details', 'Results', 'Diff']}
      >
        <DetailsTab audit={audit.data} />
        <ResultTab />
        <DiffTab />
      </DetailPage>
    </>
  )
}
